import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import API, { parseError } from '../utils/api';
import { Color } from '../constants';
import { Link } from 'react-router-dom';

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail]   = useState('');
  const [code, setCode]     = useState('');
  const [step, setStep]     = useState('email');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [message, setMessage] = useState('');

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await API.post('/auth/request-otp', { email: email.trim() });
      setStep('code');
      setMessage(`Code sent to ${email}`);
    } catch (err) {
      // Always advance to OTP screen — don't reveal if email exists
      setStep('code');
      setMessage(`Code sent to ${email}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/auth/verify-otp', {
        email: email.trim(),
        code: code.trim(),
      });
      login(res.data.user, res.data.access_token);
    } catch (err) {
      setError(parseError(err, 'Invalid or expired code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(/bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
      <div style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(15, 0, 105, 0.45)',
        zIndex: 0,
      }} />

      <div className="rounded-2xl shadow-lg w-full max-w-md p-8"
        style={{ backgroundColor: 'rgba(255,255,255,0.92)', position: 'relative', zIndex: 1 }}>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: Color.NAVY }}>
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: Color.NAVY }}>
            Work Orders App
          </h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to your account</p>
        </div>

        {/* Step 1 — Email */}
        {step === 'email' && (
          <form onSubmit={handleRequestOTP}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your work email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': Color.LIGHT_BLUE }}
              />
            </div>
            {error && <ErrorBox message={error} />}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-medium text-white transition
                         disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: Color.NAVY }}
            >
              {loading ? 'Sending...' : 'Send Login Code'}
            </button>
          </form>
        )}

        {/* Step 2 — OTP */}
        {step === 'code' && (
          <form onSubmit={handleVerifyOTP}>
            {message && (
              <div className="mb-4 p-3 rounded-lg text-sm"
                style={{ backgroundColor: '#E3F4FB', color: '#0077A3' }}>
                {message}
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter 6-digit code
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 rounded-lg
                           text-center text-2xl font-bold tracking-widest
                           focus:outline-none focus:ring-2 focus:border-transparent"
              />
            </div>
            {error && <ErrorBox message={error} />}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-medium text-white transition
                         disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: Color.NAVY }}
            >
              {loading ? 'Verifying...' : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('email'); setError(''); setCode(''); }}
              className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
            >
              ← Use a different email
            </button>
          </form>
        )}

        {/* Privacy Policy link */}
        <p className="text-center text-xs text-gray-400 mt-6">
          This system is for authorized use only.{' '}
          <Link to="/privacy" className="underline hover:text-gray-600">
            Privacy Policy
          </Link>
        </p>

      </div>
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="mb-4 p-3 rounded-lg text-sm border"
      style={{ backgroundColor: '#FDECEA', borderColor: '#FFCDD2', color: '#A00028' }}>
      {message}
    </div>
  );
}